import { FunctionComponent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { format } from "date-fns";

import {
	faInbox,
	faPaperPlaneTop,
	faList,
	faAddressBook,
	faClock,
} from "@fortawesome/pro-regular-svg-icons";

import { useAppDispatch } from "@/redux/hooks";
import { closeContactLeadPanel } from "@/pages/Private/redux/contact/contact.slice";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { ListPersonStatus } from "@/pages/Private/pages/List/components/ListPersonStatus";

import { ContactListInfo, ContactListPerson } from "../schema/contacts";
import { LeadLocationKind, resolveLeadLocation } from "../utils/resolveLeadLocation";

const KIND_ICON: Record<LeadLocationKind, typeof faInbox> = {
	INBOX: faInbox,
	OUTBOX: faPaperPlaneTop,
	LIST: faList,
	CONTACTS: faAddressBook,
};

// The date of the lead's last status change on the list (`updatedAt`), formatted like "12 May 2026".
const formatStatusDate = (iso?: string): string => {
	if (!iso) {
		return "";
	}

	const date = new Date(iso);

	return isNaN(date.getTime()) ? "" : format(date, "d MMM yyyy");
};

interface ContactLeadLocationProps {
	listPersons?: ContactListPerson[];
	lists?: ContactListInfo[];
	prospectName?: string | null;
	// While the detail query is still loading the preview lacks list memberships; suppress the
	// "only in Contacts" fallback until we actually know there are none.
	isDetailLoading?: boolean;
	// When set (Chrome extension), the panel lives outside the web app's router (in a MemoryRouter),
	// so in-app `navigate` goes nowhere. Open the destination in the web app in a new tab instead.
	webAppUrl?: string;
	// On surfaces that already show the lead inside a list/detail view, render nothing instead of the
	// "only in Contacts" fallback when there are no memberships to display.
	hideWhenEmpty?: boolean;
	// Override the section heading translation key (defaults to the "In lists" placement title).
	titleKey?: string;
	// Invoked right before navigating away. Host panels that aren't the Contacts lead panel
	// (e.g. the list DetailView) close themselves here — `go` only knows how to close that one.
	onNavigate?: () => void;
}

export const ContactLeadLocation: FunctionComponent<ContactLeadLocationProps> = ({
	listPersons,
	lists,
	prospectName,
	isDetailLoading = false,
	webAppUrl,
	hideWhenEmpty = false,
	titleKey = "contacts.leadPanel.placement.inListsTitle",
	onNavigate,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const go = (to: string) => {
		dispatch(closeContactLeadPanel());
		onNavigate?.();

		if (webAppUrl) {
			window.open(`${webAppUrl}${to}`, "_blank", "noopener");

			return;
		}

		navigate(to);
	};

	const memberships = (listPersons ?? [])
		.filter((lp) => typeof lp.listId === "number")
		.map((lp) => {
			const list = lists?.find((l) => l.id === lp.listId);
			const location = resolveLeadLocation({
				assignStatus: lp.assignStatus,
				listId: lp.listId,
				listName: list?.name,
				prospectName,
			});

			return {
				key: lp.id,
				listName: list?.name ?? "",
				assignStatus: lp.assignStatus,
				statusDate: formatStatusDate(lp.updatedAt),
				location,
			};
		});

	// Nothing to show yet — render nothing to avoid a flash of the fallback (while loading) or any
	// chrome at all on surfaces that opt into hiding the empty state.
	if (memberships.length === 0 && (isDetailLoading || hideWhenEmpty)) {
		return null;
	}

	return (
		<div className="mb-3.5">
			<p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
				{t(titleKey)}
			</p>

			{memberships.length === 0 ? (
				<Button
					title={t("contacts.leadPanel.placement.viewInContacts")}
					color={ButtonColor.ACTION_SECONDARY}
					size={ButtonSize.XS}
					image={<Icon className="mr-2" icon={faAddressBook} />}
					onClick={() => go(resolveLeadLocation({}).to)}
				/>
			) : (
				<div className="overflow-hidden rounded-lg border border-border-light bg-white">
					{memberships.map((m) => (
						<button
							key={m.key}
							type="button"
							className="flex w-full cursor-pointer items-center gap-2 border-b border-border-light px-3 py-2 text-left last:border-b-0 hover:bg-brand-white"
							onClick={() => go(m.location.to)}
						>
							<Icon
								className="shrink-0 text-xs text-text-tertiary"
								icon={KIND_ICON[m.location.kind]}
							/>
							<span className="min-w-0 flex-1 truncate text-xs font-semibold text-text">
								{m.listName || t(`contacts.leadPanel.placement.kind.${m.location.kind}`)}
							</span>
							{m.statusDate && (
								<span className="flex shrink-0 items-center gap-1 text-xxs text-text-tertiary">
									<Icon className="text-[10px]" icon={faClock} />
									{m.statusDate}
								</span>
							)}
							{m.assignStatus && <ListPersonStatus size="sm" status={m.assignStatus} />}
						</button>
					))}
				</div>
			)}
		</div>
	);
};
